import { useEffect, useState } from "react";
import { Crest, TopBar } from "@/components/bigscreen/Scoreboard";
import { goalSide, minuteLabel, GOAL_TYPES } from "@/lib/bigScreen";
import { formatMatchDate } from "@/lib/dates";

const Panel = ({ title, children, testId }) => (
  <div className="flex-1 flex flex-col bs-fade" data-testid={testId}>
    {children[0]}
    <p className="font-heading font-black uppercase text-[#FFC72C] text-[44px] tracking-[0.25em] mt-8 mb-6 text-center">{title}</p>
    <div className="flex-1 min-h-0">{children[1]}</div>
  </div>
);

const ScoreStrip = ({ centre }) => {
  const { match, home_team: home, away_team: away } = centre;
  return (
    <div className="flex items-center justify-center gap-10">
      <Crest team={home} size={120} />
      <p className="font-heading font-black uppercase text-white text-[52px] w-[520px] text-right truncate">{home?.name}</p>
      <p className="font-heading font-black text-white text-[120px] tabular-nums leading-none" data-testid="bs-strip-score">{match.home_score ?? 0} – {match.away_score ?? 0}</p>
      <p className="font-heading font-black uppercase text-white text-[52px] w-[520px] truncate">{away?.name}</p>
      <Crest team={away} size={120} />
    </div>
  );
};

// ---- GOAL! ----
export const GoalCelebration = ({ centre, event }) => {
  const side = goalSide(event, centre.match);
  const team = side === "home" ? centre.home_team : centre.away_team;
  return (
    <div className="absolute z-30 flex flex-col items-center justify-center bs-goal" style={{ inset: "-54px -96px" }} data-testid="bs-goal-celebration">
      <div className="absolute inset-0 bg-[#030a17]/95" />
      <div className="relative flex flex-col items-center">
        <p className="font-heading font-black italic text-[300px] leading-none text-[#FFC72C] bs-goal-word">GOAL!</p>
        <div className="flex items-center gap-8 mt-6">
          <Crest team={team} size={170} />
          <div>
            <p className="font-heading font-black uppercase text-white text-[80px] leading-none" data-testid="bs-goal-scorer">{event.player_name || team?.name}</p>
            <p className="text-[38px] text-white/70 mt-3" data-testid="bs-goal-detail">
              {team?.name} · {minuteLabel(event)}{event.type === "penalty_goal" ? " · Penalty" : event.type === "own_goal" ? " · Own goal" : ""}
            </p>
          </div>
        </div>
        <p className="font-heading font-black text-white text-[150px] tabular-nums mt-10 leading-none" data-testid="bs-goal-score">
          {centre.match.home_score ?? 0} – {centre.match.away_score ?? 0}
        </p>
      </div>
    </div>
  );
};

// ---- cards / subs lower-third ----
const BANNER = { yellow_card: ["Yellow Card", "bg-[#FFC72C] text-black"], red_card: ["Red Card", "bg-[#E10600] text-white"], substitution: ["Substitution", "bg-[#009E60] text-white"] };
export const EventBanner = ({ centre, event }) => {
  const [label, cls] = BANNER[event.type];
  const team = event.team_id === centre.match.home_team_id ? centre.home_team : centre.away_team;
  return (
    <div className="absolute left-[96px] right-[96px] bottom-[54px] z-20 flex items-stretch bs-lower-third" data-testid={`bs-banner-${event.type}`}>
      <div className={`px-10 flex items-center font-heading font-black uppercase text-[44px] tracking-widest ${cls}`}>{label}</div>
      <div className="flex-1 flex items-center gap-6 px-8 py-5 bg-[#0b1a33]/95 ring-1 ring-white/10">
        {team?.logo_url && <img src={team.logo_url} alt="" className="w-[70px] h-[70px] object-contain" />}
        <p className="font-heading font-bold text-white text-[48px] uppercase truncate">
          {event.type === "substitution"
            ? <>On: {event.player_name || "—"} <span className="text-white/50 mx-4">|</span> Off: {event.secondary_player_name || "—"}</>
            : event.player_name || team?.name}
        </p>
        <p className="ml-auto font-heading font-black text-[52px] text-white/80 tabular-nums">{minuteLabel(event)}</p>
      </div>
    </div>
  );
};

// ---- line-ups ----
const XI = ({ lineup, team }) => (
  <div className="flex-1 bg-white/[0.04] ring-1 ring-white/10 p-8 min-w-0">
    <div className="flex items-center gap-5 mb-5">
      <Crest team={team} size={90} />
      <div className="min-w-0">
        <p className="font-heading font-black uppercase text-white text-[44px] leading-none truncate">{team?.name}</p>
        <p className="text-[26px] text-white/50">{[lineup?.formation, lineup?.coach_name && `Coach: ${lineup.coach_name}`].filter(Boolean).join(" · ")}</p>
      </div>
    </div>
    {lineup?.starters?.length ? (
      <div className="grid grid-cols-1 gap-y-1">
        {lineup.starters.slice(0, 11).map((p, i) => (
          <div key={p.player_id || i} className="flex items-center gap-5 text-[32px] text-white">
            <span className="w-14 font-heading font-black text-[#4FD1E8] tabular-nums text-right">{p.number ?? ""}</span>
            <span className="truncate">{p.player_name}</span>
            {p.position === "GK" && <span className="text-[20px] font-bold px-2 bg-[#FFC72C] text-black">GK</span>}
            {p.is_captain && <span className="text-[20px] font-bold px-2 ring-1 ring-white/50">C</span>}
          </div>
        ))}
      </div>
    ) : <p className="text-[30px] text-white/40 mt-10">Line-up not yet announced</p>}
  </div>
);

export const LineupsView = ({ centre }) => (
  <Panel title="Starting Line-ups" testId="bs-lineups">
    <TopBar centre={centre} />
    <div className="flex gap-10 h-full">
      <XI lineup={centre.lineup_home} team={centre.home_team} />
      <XI lineup={centre.lineup_away} team={centre.away_team} />
    </div>
  </Panel>
);

// ---- statistics (only recorded values) ----
const STAT_ROWS = [["possession", "Possession", "%"], ["shots", "Shots"], ["shots_on_target", "Shots on Target"], ["corners", "Corners"], ["fouls", "Fouls"], ["yellow_cards", "Yellow Cards"], ["red_cards", "Red Cards"], ["offsides", "Offsides"]];

export const statRows = (centre) => {
  const s = centre.statistics || {};
  const cards = (type, side) => centre.events.filter((e) => e.type === type && (e.team_id === centre.match[`${side}_team_id`])).length;
  return STAT_ROWS.map(([k, label, unit]) => {
    let h = s[`${k}_home`], a = s[`${k}_away`];
    if ((k === "yellow_cards" || k === "red_cards") && h == null && a == null) {
      const type = k === "yellow_cards" ? "yellow_card" : "red_card";
      h = cards(type, "home"); a = cards(type, "away");
      if (!h && !a && !centre.events.length) return null;
    }
    return h == null && a == null ? null : { label, h: h ?? 0, a: a ?? 0, unit: unit || "" };
  }).filter(Boolean);
};

const StatBars = ({ rows }) => (
  <div className="max-w-[1400px] mx-auto w-full space-y-6">
    {rows.map((r) => {
      const total = r.h + r.a || 1;
      return (
        <div key={r.label} data-testid={`bs-stat-${r.label.toLowerCase().replace(/ /g, "-")}`}>
          <div className="flex justify-between font-heading font-black text-white text-[48px] leading-none mb-2">
            <span className="tabular-nums">{r.h}{r.unit}</span>
            <span className="uppercase tracking-[0.2em] text-[30px] text-white/60 self-center">{r.label}</span>
            <span className="tabular-nums">{r.a}{r.unit}</span>
          </div>
          <div className="flex h-4 gap-2">
            <div className="flex-1 flex justify-end bg-white/[0.06]"><div className="bg-[#4FD1E8] h-full" style={{ width: `${(r.h / total) * 100}%` }} /></div>
            <div className="flex-1 bg-white/[0.06]"><div className="bg-[#FFC72C] h-full" style={{ width: `${(r.a / total) * 100}%` }} /></div>
          </div>
        </div>
      );
    })}
  </div>
);

export const StatsView = ({ centre, title = "Match Statistics" }) => {
  const rows = statRows(centre);
  return (
    <Panel title={title} testId="bs-stats">
      <ScoreStrip centre={centre} />
      {rows.length ? <StatBars rows={rows} /> : <p className="text-center text-[36px] text-white/40 mt-16" data-testid="bs-stats-empty">Statistics have not been recorded for this match</p>}
    </Panel>
  );
};

// ---- half-time / full-time ----
const GOALS_PER_SIDE = 4;
// Scorers grouped per player ("Smith 12', 67'") and capped so stats never get pushed off-screen
const GoalList = ({ centre }) => {
  const goals = centre.events.filter((e) => GOAL_TYPES.includes(e.type));
  if (!goals.length) return null;
  const grouped = (side) => {
    const map = new Map();
    goals.filter((g) => goalSide(g, centre.match) === side).forEach((g) => {
      const key = `${g.player_name || "Goal"}${g.type === "own_goal" ? " (OG)" : ""}`;
      map.set(key, [...(map.get(key) || []), `${minuteLabel(g)}${g.type === "penalty_goal" ? " P" : ""}`]);
    });
    return [...map.entries()];
  };
  return (
    <div className="grid grid-cols-2 gap-16 max-w-[1500px] mx-auto w-full mt-4" data-testid="bs-goal-list">
      {["home", "away"].map((side) => {
        const rows = grouped(side);
        return (
          <div key={side} className={`space-y-1 text-[28px] text-white/80 ${side === "home" ? "text-right" : ""}`}>
            {rows.slice(0, GOALS_PER_SIDE).map(([name, mins]) => <p key={name} className="truncate">{name} {mins.join(", ")}</p>)}
            {rows.length > GOALS_PER_SIDE && <p className="text-white/50">+{rows.length - GOALS_PER_SIDE} more</p>}
          </div>
        );
      })}
    </div>
  );
};

export const BreakView = ({ centre, kind }) => {
  const rows = statRows(centre).slice(0, 3);
  const pens = centre.shootout?.home_score != null;
  return (
    <div className="flex-1 flex flex-col bs-fade" data-testid={`bs-${kind}`}>
      <TopBar centre={centre} />
      <p className="font-heading font-black uppercase text-center text-[#FFC72C] text-[110px] tracking-[0.15em] leading-none mt-10" data-testid={`bs-${kind}-title`}>
        {kind === "fulltime" ? "Full-Time" : "Half-Time"}
      </p>
      <div className="mt-10"><ScoreStrip centre={centre} /></div>
      {kind === "fulltime" && pens && (
        <p className="text-center font-heading font-bold text-[48px] text-[#4FD1E8] mt-4">Penalties {centre.shootout.home_score} – {centre.shootout.away_score}</p>
      )}
      <GoalList centre={centre} />
      {rows.length > 0 && <div className="mt-auto"><StatBars rows={rows} /></div>}
    </div>
  );
};

// ---- upcoming ----
export const UpcomingView = ({ upcoming }) => (
  <div className="flex-1 flex flex-col items-center justify-center bs-fade" data-testid="bs-upcoming">
    <p className="font-heading font-black uppercase text-[#FFC72C] text-[56px] tracking-[0.3em]">Next Match</p>
    {upcoming ? (
      <>
        <p className="text-[34px] text-white/60 mt-3 uppercase tracking-widest">{upcoming.competition?.name}</p>
        <div className="flex items-center gap-20 mt-16">
          <div className="flex flex-col items-center gap-6 w-[520px]"><Crest team={upcoming.home_team} size={240} /><p className="font-heading font-black uppercase text-white text-[60px] text-center leading-none">{upcoming.home_team?.name}</p></div>
          <p className="font-heading font-black text-[120px] text-white/40">VS</p>
          <div className="flex flex-col items-center gap-6 w-[520px]"><Crest team={upcoming.away_team} size={240} /><p className="font-heading font-black uppercase text-white text-[60px] text-center leading-none">{upcoming.away_team?.name}</p></div>
        </div>
        <p className="font-heading font-bold text-[56px] text-white mt-16" data-testid="bs-upcoming-kickoff">
          {formatMatchDate(upcoming.match.match_date, { weekday: "long", day: "numeric", month: "long" })}{upcoming.match.match_time ? ` · ${upcoming.match.match_time}` : ""}
        </p>
        {upcoming.match.stadium && <p className="text-[36px] text-white/50 mt-2">{upcoming.match.stadium}</p>}
      </>
    ) : <p className="text-[40px] text-white/40 mt-10" data-testid="bs-upcoming-empty">No upcoming fixture scheduled</p>}
  </div>
);

// ---- standings ----
export const StandingsView = ({ centre, rows }) => {
  const ids = [centre?.match?.home_team_id, centre?.match?.away_team_id];
  const big = rows.length > 12;
  return (
    <Panel title="League Table" testId="bs-standings">
      <TopBar centre={centre} />
      {rows.length ? (
        <table className={`w-full max-w-[1500px] mx-auto text-white ${big ? "text-[24px]" : "text-[32px]"}`}>
          <thead><tr className="text-white/50 uppercase tracking-widest text-[22px]">{["#", "Club", "P", "W", "D", "L", "GD", "Pts"].map((h) => <th key={h} className={`py-2 ${h === "Club" ? "text-left" : "text-center"}`}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.team_id} className={`border-t border-white/10 ${ids.includes(r.team_id) ? "bg-[#4FD1E8]/15" : ""}`} data-testid={`bs-standing-${r.team_id}`}>
                <td className="text-center font-heading font-black py-1.5 w-20">{r.played ? r.position : "–"}</td>
                <td className="flex items-center gap-4 py-1.5">{r.logo_url && <img src={r.logo_url} alt="" className={big ? "w-8 h-8" : "w-11 h-11"} />}<span className="font-semibold">{r.team_name}</span></td>
                {[r.played, r.won, r.drawn, r.lost, r.goal_difference > 0 ? `+${r.goal_difference}` : r.goal_difference].map((v, i) => <td key={i} className="text-center tabular-nums">{v}</td>)}
                <td className="text-center font-heading font-black tabular-nums">{r.points}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <p className="text-center text-[36px] text-white/40" data-testid="bs-standings-empty">Standings are not available for this competition</p>}
    </Panel>
  );
};

// ---- sponsor / announcement slides (rotate) ----
export const SlidesView = ({ slides, seconds = 10 }) => {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (slides.length < 2) return undefined;
    const iv = setInterval(() => setI((x) => (x + 1) % slides.length), seconds * 1000);
    return () => clearInterval(iv);
  }, [slides.length, seconds]);
  if (!slides.length) return null;
  const s = slides[i % slides.length];
  return (
    <div key={s.slide_id} className="flex-1 flex flex-col items-center justify-center text-center bs-fade" data-testid={`bs-slide-${s.slide_id}`}>
      {s.kind === "sponsor" && s.image_url && <img src={s.image_url} alt={s.title || "Sponsor"} className="max-w-[1500px] max-h-[720px] object-contain" />}
      {s.title && <p className={`font-heading font-black uppercase text-white leading-none ${s.kind === "sponsor" ? "text-[56px] mt-10" : "text-[120px]"}`}>{s.title}</p>}
      {s.body && <p className="text-[44px] text-white/70 mt-8 max-w-[1500px] whitespace-pre-line">{s.body}</p>}
    </div>
  );
};
